/**
 * Matching free-text activity names against the entity catalog — and against
 * each other.
 *
 * Activities can be logged without a place: typed in the moment ("Hooks"), or
 * transcribed from a note long after the trip. Those entries carry a `name`
 * and no `entityId`, so they never join the catalog: two nights at the same
 * bar stay two unrelated rows, and nothing about them rolls up to the place.
 *
 * Fixing that after the fact needs two answers, both of which come down to
 * "are these two names the same thing?":
 *
 *   - which catalog entity does "Hook's Point" mean?
 *   - which other unlinked activities mean the same thing it does?
 *
 * The similarity here is deliberately boring — normalize, then blend a bigram
 * score with token containment. It has to be explainable when it's wrong,
 * because a person reviews every suggestion before anything is written.
 */

/**
 * Leading words that describe *what happened* rather than *where*. A note
 * reading "Dinner at Space 220" is about Space 220; the meal is noise. Only
 * stripped when followed by "at", so a plain "Dinner" keeps its name.
 */
const ACTIVITY_PREFIXES = [
  "breakfast", "brunch", "lunch", "dinner", "supper", "snack", "snacks",
  "dessert", "drinks", "drink", "coffee", "meal", "ate", "eat", "dined",
  "show", "ride", "rode", "riding", "visit", "visited", "stop", "stopped",
];

/** Words that carry no identity of their own when comparing two names. */
const STOP_TOKENS = new Set(["the", "a", "an", "of", "at", "in", "on", "and", "de", "la", "le"]);

/**
 * Lowercase, de-accent, and reduce punctuation to spaces. "Hook's Point" and
 * "Hooks Point" have to land on the same string, and "Crêperie" has to match
 * what someone typed on a phone keyboard without the circumflex.
 */
export function normalizeName(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/&/g, " and ")
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

/**
 * Drop a leading activity word when the name says "<activity> at <place>".
 * Returns the input unchanged when there's nothing to strip.
 */
function stripActivityPrefix(normalized: string): string {
  const at = normalized.indexOf(" at ");
  if (at <= 0) return normalized;
  const head = normalized.slice(0, at).trim();
  if (!ACTIVITY_PREFIXES.includes(head)) return normalized;
  const tail = normalized.slice(at + 4).trim();
  return tail || normalized;
}

/**
 * Comparison tokens: normalized, stripped of stop words, and lightly
 * singularized so "Hooks" and "Hook" are the same token. Falls back to the
 * unfiltered tokens when a name is nothing but stop words ("The Ride").
 */
export function nameTokens(raw: string | null | undefined): string[] {
  const words = stripActivityPrefix(normalizeName(raw)).split(" ").filter(Boolean);
  const kept = words.filter((w) => !STOP_TOKENS.has(w));
  const base = kept.length > 0 ? kept : words;
  return base.map((w) => (w.length > 3 && w.endsWith("s") && !w.endsWith("ss") ? w.slice(0, -1) : w));
}

/** A name prepared for repeated comparison — see `prepareName`. */
export interface PreparedName {
  /** The name as it was given, for display. */
  raw: string;
  /** Normalized, activity-prefix stripped, spaces removed — the bigram source. */
  compact: string;
  tokens: string[];
  /** Distinct tokens, for containment. */
  tokenSet: Set<string>;
}

/**
 * Pre-compute everything `similarity` needs. Clustering compares every pair,
 * so normalizing inside the loop is the difference between instant and
 * sluggish on a few hundred activities.
 */
export function prepareName(raw: string | null | undefined): PreparedName {
  const tokens = nameTokens(raw);
  return {
    raw: raw ?? "",
    compact: tokens.join(""),
    tokens,
    tokenSet: new Set(tokens),
  };
}

/** Sørensen–Dice coefficient over character bigrams. */
function diceCoefficient(a: string, b: string): number {
  if (a === b) return a.length > 0 ? 1 : 0;
  if (a.length < 2 || b.length < 2) return 0;
  const counts = new Map<string, number>();
  for (let i = 0; i < a.length - 1; i++) {
    const g = a.slice(i, i + 2);
    counts.set(g, (counts.get(g) ?? 0) + 1);
  }
  let hits = 0;
  for (let i = 0; i < b.length - 1; i++) {
    const g = b.slice(i, i + 2);
    const left = counts.get(g) ?? 0;
    if (left > 0) {
      counts.set(g, left - 1);
      hits++;
    }
  }
  return (2 * hits) / (a.length - 1 + b.length - 1);
}

/**
 * How much of the shorter name's vocabulary the longer one contains. This is
 * what lets "Space Mountain" match "Space Mountain (Lightning Lane)" — the
 * extra qualifier drags the bigram score down but not the containment.
 */
function tokenContainment(a: PreparedName, b: PreparedName): number {
  const smaller = a.tokenSet.size <= b.tokenSet.size ? a.tokenSet : b.tokenSet;
  const larger = smaller === a.tokenSet ? b.tokenSet : a.tokenSet;
  if (smaller.size === 0) return 0;
  let hits = 0;
  for (const t of smaller) if (larger.has(t)) hits++;
  return hits / smaller.size;
}

/**
 * Words that describe a *kind* of place or evening rather than a particular
 * one. Drawn from what people actually type: in this app's own backlog,
 * "trivia" appears in 26 different activity names, "lounge" in 19, "bar" in
 * 11. Left to itself, a bare "Lounge" is contained in "Cadillac Lounge",
 * "Dahlia Lounge" and every other one, so it matches all of them equally —
 * which means it identifies none of them.
 *
 * A name made only of these can still be a perfectly good *group* ("Breakfast",
 * logged thirteen times), it just can't claim to be the same thing as a longer
 * name that happens to contain it.
 */
const GENERIC_TOKENS = new Set([
  "lounge", "bar", "pub", "cafe", "coffee", "restaurant", "dining", "diner",
  "grill", "kitchen", "room", "club", "house", "deck", "pool", "beach",
  "show", "trivia", "game", "party", "parade", "tasting", "class", "tour",
  "breakfast", "brunch", "lunch", "dinner", "supper", "snack", "dessert",
  "drink", "meal", "food", "flight", "hotel", "resort", "ship", "port",
  "arrival", "departure", "transfer", "check", "day", "morning", "evening",
  "night", "activity", "event", "stop", "visit", "time", "session",
]);

/** The shortest word that can identify a place on its own. */
const MIN_ANCHOR_LENGTH = 3;

/**
 * True when the smaller of the two names carries at least one word specific
 * enough to anchor a match.
 *
 * "Hooks" anchors — it names one bar. "Lounge" does not, so "Lounge" sitting
 * inside "Cadillac Lounge" stays a weak character-level resemblance instead of
 * being promoted to a match.
 */
function hasAnchor(a: PreparedName, b: PreparedName): boolean {
  const smaller = a.tokenSet.size <= b.tokenSet.size ? a : b;
  for (const token of smaller.tokenSet) {
    if (!GENERIC_TOKENS.has(token) && token.length >= MIN_ANCHOR_LENGTH) return true;
  }
  return false;
}

/**
 * Similarity (0–1) between two already-prepared names.
 *
 * The two signals are combined rather than averaged, because they mean
 * different things. One name's words being *entirely* contained in the other's
 * is the signature of a qualifier ("Space Mountain" / "Space Mountain
 * (Lightning Lane)") and is treated as a match outright. Words merely
 * *overlapping* is the signature of a shared generic word — "Space Mountain"
 * and "Splash Mountain" share "Mountain" and are not the same ride — so a
 * partial overlap discounts the character score instead of lifting it.
 */
export function similarity(a: PreparedName, b: PreparedName): number {
  if (!a.compact || !b.compact) return 0;
  if (a.compact === b.compact) return 1;
  const dice = diceCoefficient(a.compact, b.compact);
  const contained = tokenContainment(a, b);
  if (contained === 1) {
    // Total containment cuts both ways. Anchored, it's a qualifier on a name
    // we recognize. Unanchored, the containing name swallows a word that
    // describes a category — the resemblance is not just uninformative, it is
    // actively misleading, so it's damped rather than merely ignored.
    return hasAnchor(a, b) ? 0.85 + 0.15 * dice : dice * 0.5;
  }
  return dice * (0.5 + 0.5 * contained);
}

/** Similarity (0–1) between two raw names. 1 means "the same name". */
export function nameSimilarity(a: string | null | undefined, b: string | null | undefined): number {
  return similarity(prepareName(a), prepareName(b));
}

/**
 * Confident enough to pre-select for the reviewer. Below it, a suggestion is
 * offered but never checked by default.
 */
export const MATCH_STRONG = 0.85;
/** Worth showing at all. Anything lower is noise. */
export const MATCH_POSSIBLE = 0.55;
/** How alike two names must be before one is offered as related to the other. */
export const RELATED_THRESHOLD = 0.85;

export interface ScoredMatch<T> {
  item: T;
  score: number;
  /** True when the names are identical once normalized. */
  exact: boolean;
}

/** A candidate with its name already prepared — see `prepareCandidates`. */
export interface PreparedCandidate<T> {
  item: T;
  prepared: PreparedName;
}

/**
 * Prepare a candidate list once so it can be ranked against many names. The
 * catalog is the same for every group on the review page; re-normalizing all
 * of it per group is the difference between a snappy list and a janky one.
 */
export function prepareCandidates<T>(
  candidates: readonly T[],
  getName: (item: T) => string,
): PreparedCandidate<T>[] {
  return candidates.map((item) => ({ item, prepared: prepareName(getName(item)) }));
}

/**
 * Rank prepared candidates against a name, best first. Ties break on the
 * candidate's own name so the order is stable across renders.
 */
export function rankPrepared<T>(
  name: string | null | undefined,
  candidates: readonly PreparedCandidate<T>[],
  options: { limit?: number; minScore?: number } = {},
): ScoredMatch<T>[] {
  const { limit = 5, minScore = MATCH_POSSIBLE } = options;
  const target = prepareName(name);
  if (!target.compact) return [];

  const scored: (ScoredMatch<T> & { name: string })[] = [];
  for (const { item, prepared } of candidates) {
    const score = similarity(target, prepared);
    if (score >= minScore) {
      scored.push({ item, score, exact: prepared.compact === target.compact, name: prepared.raw });
    }
  }
  scored.sort((a, b) => b.score - a.score || a.name.localeCompare(b.name));
  return scored.slice(0, limit).map(({ item, score, exact }) => ({ item, score, exact }));
}

/** Rank candidates by how well their name matches `name`, best first. */
export function rankByName<T>(
  name: string | null | undefined,
  candidates: readonly T[],
  getName: (item: T) => string,
  options: { limit?: number; minScore?: number } = {},
): ScoredMatch<T>[] {
  return rankPrepared(name, prepareCandidates(candidates, getName), options);
}

export interface NameGroup<T> {
  /** Stable id for the group — its normalized token key. */
  key: string;
  /** The name shown for the group: the spelling most entries actually used. */
  label: string;
  /** Every distinct spelling in the group, label first. */
  variants: string[];
  items: T[];
}

/**
 * Group activities that were written down the same way.
 *
 * Sameness here is exact after normalization — case, accents, punctuation,
 * "&", a leading "Dinner at", and plural/singular are all ignored, so "Hooks",
 * "hooks" and "Hook's" are one group, but "Hooks" and "Hooks Barbery" are not.
 *
 * This deliberately does *not* merge names that merely look alike, which an
 * earlier version did (transitively, via union-find) and which failed badly on
 * real data: a bare "Lounge" is contained in nineteen different lounge names,
 * so it chained every lounge on the ship into one group of 73, and a bare
 * "Trivia" did the same to every trivia night. Transitive merging turns one
 * over-eager pair into a group nobody can review.
 *
 * Names that look alike are surfaced by `relatedGroups` instead, as a
 * suggestion next to the group — visible, attributable, and only merged if
 * someone says so.
 */
export function groupByName<T>(
  items: readonly T[],
  getName: (item: T) => string,
): NameGroup<T>[] {
  const buckets = new Map<string, { prepared: PreparedName[]; items: T[] }>();
  for (const item of items) {
    const prepared = prepareName(getName(item));
    const key = prepared.compact || `\u0000${prepared.raw.trim().toLowerCase()}`;
    const bucket = buckets.get(key) ?? { prepared: [], items: [] };
    bucket.prepared.push(prepared);
    bucket.items.push(item);
    buckets.set(key, bucket);
  }

  const groups: NameGroup<T>[] = [];
  for (const [key, bucket] of buckets) {
    const variants = rankSpellings(bucket.prepared.map((p) => p.raw));
    groups.push({ key, label: variants[0] ?? "Unnamed", variants, items: bucket.items });
  }
  groups.sort((a, b) => b.items.length - a.items.length || a.label.localeCompare(b.label));
  return groups;
}

/**
 * Other groups whose names look like this one's — "Palo Brunch" and "Palo
 * Dinner" next to "Palo". Offered so a person can pull them in, never applied
 * on their own.
 */
export function relatedGroups<T>(
  group: NameGroup<T>,
  all: readonly NameGroup<T>[],
  options: { limit?: number; minScore?: number } = {},
): ScoredMatch<NameGroup<T>>[] {
  const others = all.filter((g) => g.key !== group.key);
  return rankByName(group.label, others, (g) => g.label, {
    limit: options.limit ?? 3,
    minScore: options.minScore ?? MATCH_STRONG,
  });
}

/**
 * Distinct spellings, most-used first, then longest — the fullest version of a
 * name is usually the most correct ("Hook's Point Bar" over "Hooks").
 */
function rankSpellings(names: string[]): string[] {
  const counts = new Map<string, number>();
  for (const n of names) {
    const trimmed = (n ?? "").trim();
    if (trimmed) counts.set(trimmed, (counts.get(trimmed) ?? 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1] || b[0].length - a[0].length || a[0].localeCompare(b[0]))
    .map(([name]) => name);
}
